import React, { useState, useEffect, useMemo } from "react";
import { MdOutlineEdit, MdSearchOff, MdFilterList } from "react-icons/md";
import Pagination from "../Pagination/Pagination";
import OptionsMenu from "../OptionsMenu/OptionsMenu";
import Badge from "../Badge/Badge";
import "./Table.scss";
import InputSearch from "../Inputs/InputSearch/InputSearch";
import { BadgeProps, FilterField, FormGroup } from "../../../types/types";
import type { ReactNode } from "react";
import { useTableCRUD } from "../../../utils/useTableCRUD";
import Button from "../Button/Button";
import Modal from "../Modal/Modal";
import { TiDeleteOutline } from "react-icons/ti";
import FormModal from "../FormModal/FormModal";
import { LuArrowDownUp } from "react-icons/lu";
import { IoMdAdd } from "react-icons/io";
import { Link, useLocation } from "react-router-dom";
import {
  hasFileInData,
  mappedDataToFormData,
  sortByProperty,
} from "../../../utils/utils";
import FiltersDrawer from "../FiltersDrawer/FiltersDrawer";

type Column<T> = {
  key: keyof T | string[];
  label: string;
  visible: boolean;
  isLink?: boolean;
  sortable?: boolean;
  documentProp?: string;
  align?: "left" | "center" | "right";
  width?: string;
  type?: "text" | "number" | "date" | "status" | "badge" | "document";
  linkPath?: (row: T) => string;
  render?: (row: T) => ReactNode;
  badgeConfig?: {
    [key: string]: { variant: string; icon?: ReactNode; label?: string };
  } & { default?: { variant: string; icon?: ReactNode; label?: string } };
  badgeProps?: (row: T) => { type: string; label: string; Icon?: ReactNode };
};

type TableProps<T> = {
  columns: Column<T>[];
  dataTable: T[];
  noResultsMessage?: string;
  rowsPerPage?: number;
  topContent?: ReactNode;
  showCheckboxes?: boolean;
  searchTerms?: string[];
  deleteApiUrl?: string;
  addApiUrl?: string;
  editApiUrl?: string;
  formElements?: FormGroup[];
  actions?: string[];
  elementName?: string;
  keyProp?: string;
  sortProp?: string;
  states?: BadgeProps[];
  stateProp: string;
  filterFields?: FilterField<T>[];
  loading: boolean;
  selectedRows: Set<React.Key>;
  onSelectionChange: (newSelection: Set<React.Key>) => void;
};

const Table = <T extends { id: number }>({
  columns,
  dataTable = [],
  noResultsMessage = "No hay resultados.",
  rowsPerPage = 10,
  topContent,
  showCheckboxes = false,
  searchTerms = [],
  formElements,
  actions,
  elementName,
  keyProp = "",
  states,
  stateProp,
  addApiUrl,
  editApiUrl,
  deleteApiUrl,
  filterFields = [],
  loading = false,
  sortProp = keyProp,
  selectedRows = new Set(),
  onSelectionChange,
}: TableProps<T>) => {
  const { data, createItem, updateItem, deleteItem, isLoading, error } =
    useTableCRUD<T>(dataTable);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectAll, setSelectAll] = useState(false);
  const [filteredData, setFilteredData] = useState<T[]>(dataTable);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<T | null>(null);

  const loadingData = loading;

  const location = useLocation();
  const base = location.pathname;

  useEffect(() => {
    setDynamicFormElements(formElements || []);
  }, [formElements]);

  useEffect(() => {
    let filtered = data ?? [];
    setFilteredData(filtered);
  }, [filterValues, data, filterFields]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredData]);

  useEffect(() => {
    const start = (currentPage - 1) * rowsPerPage;
    setPaginatedData(filteredData.slice(start, start + rowsPerPage));
  }, [filteredData, currentPage, rowsPerPage]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<{ [key: string]: any }>({});
  const [modalType, setModalType] = useState("add");
  const [dynamicFormElements, setDynamicFormElements] = useState<FormGroup[]>(
    formElements || []
  );
  const [associatedOptions, setAssociatedOptions] = useState<{
    [key: string]: any[];
  }>({});

  const [paginatedData, setPaginatedData] = useState(
    dataTable?.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
  );

  // TODO: revisar si este useEffect es necesario
  //   useEffect(() => {
  //     // `crudData` será la fuente si el hook lo mantiene; en caso contrario usamos la prop
  //     setFilteredData((prev) => {
  //       const source = data ?? dataTable ?? [];
  //       // Si quieres mantener orden por defecto:
  //       return sortByProperty([...source], sortProp as keyof T);
  //     });
  //     setCurrentPage(1);
  //   }, [data, dataTable, sortProp]);

  const openModal = () => {
    setIsModalOpen(true);
    setModalType("add");
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setModalData({});
  };

  const hasFilters = (filterFields?.length ?? 0) > 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [dataTable]);

  const [sortConfig, setSortConfig] = useState<{
    key: keyof T | null;
    direction: "ascending" | "descending" | null;
  }>({
    key: null,
    direction: null,
  });

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  const sortedData = useMemo(() => {
    // Verificar si filteredData es null, undefined o está vacío
    if (!filteredData || filteredData == undefined) {
      return [];
    }

    // Crear una copia de filteredData para evitar mutaciones
    let sortableData = [...filteredData];

    // Si hay una clave y dirección de ordenación configuradas, ordenar los datos
    if (sortConfig.key && sortConfig.direction) {
      sortableData.sort((a, b) => {
        const key = sortConfig.key as keyof T;
        if (a[key] < b[key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[key] > b[key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableData;
  }, [filteredData, sortConfig]);

  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const requestSort = (key: keyof T) => {
    let direction: "ascending" | "descending" | null = "ascending";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "ascending" ? "descending" : null;
    }
    setSortConfig({ key: direction ? key : null, direction });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      onSelectionChange(new Set());
    } else {
      const allIds = new Set(filteredData.map((row) => row[keyProp as keyof T] as number));
      onSelectionChange(allIds);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectRow = (rowId: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowId)) {
      newSelected.delete(rowId);
    } else {
      newSelected.add(rowId);
    }
    onSelectionChange(newSelected);
    setSelectAll(newSelected.size === currentRows.length);
  };

  const renderStatusBadge = (status: string) => {
    const statusMap: { [key: string]: string } = {
      approved: "approved",
      "in review": "in-review",
      draft: "draft",
      rejected: "rejected",
    };

    const badgeClass = statusMap[status.toLowerCase()] || "draft";

    return <span className={`status-badge ${badgeClass}`}>{status}</span>;
  };

  const getNestedValue = (obj: any, path: keyof any | string[]): any => {
    if (Array.isArray(path)) {
      return path.reduce((acc, part) => acc && acc[part], obj);
    }
    return obj[path];
  };

  const renderCellValue = (row: T, column: Column<T>): ReactNode => {
    var value = getNestedValue(row, column.key);

    if (column.render) {
      return column.render(row);
    }

    if (column.isLink && value) {
      const href = `${base}/${(row as Record<string, any>)[keyProp]}`;
      return <Link to={href}>{String(value)}</Link>;
    }

    if (column.type === "date" && value) {
      return new Date(String(value)).toLocaleDateString();
    }

    if (column.type === "status" && value) {
      return renderStatusBadge(String(value));
    }

    if (
      column.type === "document" &&
      value &&
      column.documentProp &&
      row[column.documentProp as keyof T]
    ) {
      const base64 = row[column.documentProp as keyof T] as string;
      const mime = "application/pdf";
      const href = `data:${mime};base64,${base64}`;
      const fileName = String(value);
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          download={fileName}
        >
          {fileName || "Ver documento"}
        </a>
      );
    }

    if (stateProp == column.key && states) {
      if (states && states[(row as Record<string, any>)[stateProp]]) {
        const stateObj = states[(row as Record<string, any>)[stateProp]];
        return (
          <Badge variant={stateObj.type} Icon={stateObj.Icon}>
            {stateObj.label}
          </Badge>
        );
      }
    }

    if (value === undefined && column.key.toString().includes("/")) {
      const keys = column.key.toString().split("/");
      const key1 = keys[0];
      const key2 = keys[1];

      const nestedObject = row[key1 as keyof T] as Record<string, any>;

      if (nestedObject != undefined) {
        value = nestedObject[key2];
      }
    }

    if (
      Array.isArray(column.key) &&
      column.key.every((k) => typeof k === "string" && k.includes("/"))
    ) {
      const combinedValue = column.key
        .map((pathString) => {
          const pathArray = (pathString as string).split("/");

          const value = getNestedValue(row, pathArray);
          return value !== null && value !== undefined ? String(value) : "";
        })
        .filter((part) => part)
        .join(" - ");

      return combinedValue;
    }

    return value !== undefined &&
      value !== null &&
      (typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean")
      ? String(value)
      : value !== undefined && value !== null
      ? String(value)
      : "";
  };

  const getTableClasses = () => {
    const classes = ["custom-table"];

    classes.push(`columns-${columns.length}`);

    if (showCheckboxes) {
      classes.push("has-checkboxes");
    }

    return classes.join(" ");
  };

  const getColumnClass = (isCheckbox = false, isActions = false) => {
    if (isCheckbox) return "checkbox-column";
    if (isActions) return "actions-column";
    return "content-column";
  };

  const handleSearch = (searchTerm: String) => {
    const sourceData = data ?? [];
    if (!searchTerm) {
      setFilteredData(sourceData);
      return;
    }
    const filtered = sourceData.filter((row) =>
      Object.values(row).some(
        (value) =>
          typeof value === "string" &&
          (value as string).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredData(filtered);
  };

  const mappedData = () => {
    const defaults: { [key: string]: any } =
      formElements === undefined
        ? {}
        : formElements
            .flatMap((group) => group.elements)
            .filter(
              (el) => el.defaultValue !== undefined && el.visible === false
            )
            .reduce((acc, el) => {
              acc[el.name] = el.defaultValue;
              return acc;
            }, {} as { [key: string]: any });

    const mappedData = Object.keys(modalData).reduce(
      (acc: { [key: string]: any }, key) => {
        const value = modalData[key];
        if (value && value.id !== undefined) {
          acc[key] = value.id;
        } else {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );

    const merged = {
      ...defaults,
      ...mappedData,
    };

    const useForm = hasFileInData(merged);
    const payload = useForm ? mappedDataToFormData(merged) : merged;

    return payload;
  };

  const handleAddData = async () => {
    try {
      const formData = mappedData();
      await createItem(addApiUrl!, formData, keyProp, sortProp);

      //   let newItem: T | null = null;
      //   if (response && response.item) {
      //     newItem = response.item as T;
      //   } else {
      //     const newId = response?.newId ?? Date.now();
      //     newItem = { ...(formData as any), [keyProp]: newId } as T;
      //   }

      //   setFilteredData((prev) => {
      //     const next = sortByProperty([...prev, newItem as T], sortProp as keyof T);
      //     return next;
      //   });

      // reset modal
      setModalData({});
      closeModal();
      // opcional: saltar a última página donde aparece nuevo item
      setCurrentPage(1);
    } catch (err) {
      console.error("Error al añadir:", err);
    }
  };

  const handleEditDataAPI = async (row: T) => {
    try {
      const id = (row as Record<string, any>)[keyProp];
      const payload = mappedData();
      const response = await updateItem(
        editApiUrl!,
        id,
        payload,
        keyProp,
        sortProp
      );

      // si el backend devuelve el item actualizado, úsalo; si no, combinamos
      let updatedItem: T | null = null;
      if (response && response.item) {
        updatedItem = response.item as T;
      } else {
        updatedItem = { ...(row as any), ...(payload as any) } as T;
      }

      setFilteredData((prev) =>
        prev.map((r) =>
          (r as any)[keyProp] === (updatedItem as any)[keyProp]
            ? (updatedItem as T)
            : r
        )
      );

      closeModal();
    } catch (err) {
      console.error("Error al editar:", err);
    }
  };

  const handleDeleteData = async (row: T) => {
    setRowToDelete(row);
    setConfirmationModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      const id = (rowToDelete as Record<string, any>)[keyProp];
      await deleteItem(deleteApiUrl!, id, keyProp, sortProp);

      const newSelection = new Set(selectedRows);

      newSelection.delete(id);

      onSelectionChange(newSelection);
      setConfirmationModalOpen(false);
      setRowToDelete(null);
    } catch (err) {
      console.error("Error al eliminar:", err);
      // Opcional: mostrar un error al usuario
      setConfirmationModalOpen(false);
      setRowToDelete(null);
    }
  };

  const calculateAssociatedOptions = (currentData: { [key: string]: any }) => {
  const updatedOptions: { [key: string]: any[] } = {};
  
  formElements?.flatMap(group => group.elements).forEach(element => {
    if (element.associatedFormElement && currentData[element.associatedFormElement]) {
      const parentId = currentData[element.associatedFormElement]?.id ?? currentData[element.associatedFormElement];

      if (!parentId) {
        updatedOptions[element.name] = [];
        return;
      }

      updatedOptions[element.name] = element.options?.filter(option => {
        if (Array.isArray(option.fatherId)) {
          return option.fatherId.includes(parentId);
        }
        return option.fatherId === parentId;
      }) || [];
    }
  });
  
  return updatedOptions;
};

const handleEditData = (row: T) => {
  const initialData = { ...row };
  
  setModalData(initialData);
  
  const initialAssociatedOptions = calculateAssociatedOptions(initialData);
  setAssociatedOptions(initialAssociatedOptions);

  openModal();
  setModalType("edit");
};

const handleInputChangeModal = (name: string, value: any) => {
  const newModalData = {
    ...modalData,
    [name]: value !== null && typeof value === "object" && "value" in value
        ? value.value
        : value,
  };

  setModalData(newModalData);

  const newAssociatedOptions = calculateAssociatedOptions(newModalData);
  setAssociatedOptions(newAssociatedOptions);
};

  useEffect(() => {
    let filtered = dataTable;

    Object.entries(filterValues).forEach(([key, val]) => {
      if (
        val === undefined ||
        val === "" ||
        (Array.isArray(val) && val.length === 0)
      ) {
        return;
      }

      if (val !== undefined && val !== "") {
        const field = filterFields?.find((f) => f.key === key);
        if (!field) return;

        filtered = filtered.filter((row) => {
          const cell = row[key as keyof T];
          if (field.type === "text") {
            return String(cell)
              .toLowerCase()
              .includes(String(val).toLowerCase());
          }
          if (field.type === "number") {
            return Number(cell) === Number(val);
          }
          if (field.type === "date" && val.start && val.end) {
            return (
              new Date(String(cell)) >= val.start &&
              new Date(String(cell)) <= val.end
            );
          }
          if (field.type === "select") {
            return cell === val;
          }
          if (field.type === "multiselect" && Array.isArray(val)) {
            if (
              typeof cell === "object" &&
              cell !== null &&
              field.objectProp !== undefined
            ) {
              return val.includes(
                (cell as Record<string, any>)[field.objectProp]
              );
            }
            return val.map(x=>x.value).includes(cell);
          }

          if (field.type === "cards" && Array.isArray(val)) {
            return cell == val[0];
          }
          return true;
        });
      }
    });
    setFilteredData(filtered);
  }, [filterValues, dataTable, filterFields]);

  const clearFilters = () => {
    setFilterValues({});
    setFilteredData(dataTable);
    setIsFilterModalOpen(false);
  };

  return (
    <div className="table-card">
      <div className="table-card__top-content">
        <div style={{ flex: "1" }}>
          {searchTerms.length > 0 && (
            <div style={{ maxWidth: "400px" }}>
              <InputSearch
                placeholder={`Buscar ${elementName?.toLowerCase()}...`}
                onSearch={handleSearch}
              />
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            marginLeft: "auto",
          }}
        >
          {hasFilters && (
            <Button
              variant="ghost"
              startIcon={<MdFilterList />}
              onClick={() => setIsFilterModalOpen(true)}
              endIcon={undefined}
            >
              Filtros
            </Button>
          )}
          {formElements != undefined && formElements?.length > 0 && (
            <Button
              variant="filled"
              startIcon={<IoMdAdd />}
              onClick={openModal}
              endIcon={undefined}
            >
              {`Añadir ${elementName}`}
            </Button>
          )}
        </div>
      </div>

      <div className="table-card__content">
        <div className="table-container">
          <table className={getTableClasses()}>
            <thead>
              <tr>
                {showCheckboxes && (
                  <th className={getColumnClass(true, false)}>
                    <div
                      className={`table-checkbox ${selectAll ? "checked" : ""}`}
                      onClick={handleSelectAll}
                    />
                  </th>
                )}
                {columns
                  .filter((x) => x.visible)
                  .map((col) => (
                    <th
                      key={String(col.key)}
                      className={`${getColumnClass(false, false)} ${
                        col.sortable !== false ? "sortable" : ""
                      }`}
                      onClick={() =>
                        col.sortable !== false &&
                        requestSort(getNestedValue(col, col.key))
                      }
                    >
                      {col.label}
                      {sortConfig.key === col.key
                        ? sortConfig.direction === "ascending"
                          ? " ▲"
                          : " ▼"
                        : null}
                    </th>
                  ))}
                {actions && (
                  <th className={getColumnClass(false, true)}>Acciones</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loadingData ? (
                Array.from({ length: rowsPerPage }).map((_, rowIdx) => (
                  <tr key={`skeleton-${rowIdx}`}>
                    {showCheckboxes && (
                      <td className="checkbox-column skeleton-cell"></td>
                    )}
                    {columns
                      .filter((c) => c.visible)
                      .map((col, colIdx) => (
                        <td
                          key={`skeleton-${rowIdx}-${colIdx}`}
                          className={`content-column skeleton-cell`}
                        ></td>
                      ))}
                    {actions && (
                      <td className="actions-column skeleton-cell"></td>
                    )}
                  </tr>
                ))
              ) : currentRows.length > 0 ? (
                currentRows.map((row, index) => (
                  <tr
                    key={row[keyProp as keyof T] as number || index}
                    className={selectedRows.has(row[keyProp as keyof T] as number) ? "selected" : ""}
                  >
                    {showCheckboxes && (
                      <td className={getColumnClass(true, false)}>
                        <div
                          className={`table-checkbox ${
                            selectedRows.has(row[keyProp as keyof T] as number) ? "checked" : ""
                          }`}
                          onClick={() => handleSelectRow(row[keyProp as keyof T] as number)}
                        />
                      </td>
                    )}
                    {columns
                      .filter((x) => x.visible)
                      .map((col) => (
                        <td
                          key={`${row[keyProp as keyof T] as number || index}-${String(col.key)}`}
                          title={String(getNestedValue(row, col.key))}
                          className={`${getColumnClass(false, false)} ${
                            col.align ? `text-${col.align}` : ""
                          }`}
                          style={{
                            ...(col.width && {
                              width: col.width,
                              minWidth: col.width,
                              maxWidth: col.width,
                            }),
                          }}
                        >
                          {renderCellValue(row, col)}
                        </td>
                      ))}
                    {actions && (
                      <td>
                        <OptionsMenu
                          row={row}
                          options={
                            actions?.map((action) => {
                              if (action === "Eliminar") {
                                return {
                                  icon: <TiDeleteOutline />,
                                  label: action,
                                  action: () => handleDeleteData(row),
                                };
                              } else if (action === "Editar") {
                                return {
                                  icon: <MdOutlineEdit />,
                                  label: action,
                                  action: () => handleEditData(row),
                                };
                              } else {
                                return {
                                  label: action,
                                  action: () =>
                                    console.log(`${action} action clicked`),
                                };
                              }
                            }) ?? []
                          }
                        />
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={
                      columns.length +
                      (actions ? 1 : 0) +
                      (showCheckboxes ? 1 : 0)
                    }
                    className="no-results-cell"
                  >
                    <div className="no-results-cell__content">
                      <MdSearchOff className="no-results-cell__content__icon" />
                      <p className="no-results-cell__content__text">
                        {noResultsMessage || "No se han encontrado resultados."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="table-card__pagination">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={rowsPerPage}
              totalItems={filteredData.length}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
        <Modal isOpen={isModalOpen} onClose={closeModal} position="side">
          <h2 className="modal-title">
            {modalType === "edit"
              ? `Editar ${elementName}`
              : `Añadir ${elementName}`}
          </h2>
          <FormModal
            dynamicFormElements={dynamicFormElements}
            modalData={modalData}
            associatedOptions={associatedOptions}
            handleInputChange={handleInputChangeModal}
          />
          <div className="modal-actions">
            <Button
              variant="filled"
              onClick={
                modalType === "edit"
                  ? () => handleEditDataAPI(modalData as T)
                  : handleAddData
              }
              endIcon={undefined}
              startIcon={undefined}
            >
              Aceptar
            </Button>
            <Button
              variant="outlined"
              onClick={closeModal}
              endIcon={undefined}
              startIcon={undefined}
            >
              Cancelar
            </Button>
          </div>
        </Modal>

        {isFilterModalOpen && filterFields && hasFilters && (
          <Modal
            position="side"
            isOpen
            onClose={() => setIsFilterModalOpen(false)}
          >
            <FiltersDrawer
              filterFields={filterFields}
              data={dataTable}
              filterValues={filterValues}
              onChange={(key: any, val: any) =>
                setFilterValues((prev) => ({
                  ...prev,
                  [key]: val,
                }))
              }
              onClear={clearFilters}
            />
          </Modal>
        )}

        {confirmationModalOpen && (
          <Modal
            isOpen={confirmationModalOpen}
            onClose={() => setConfirmationModalOpen(false)}
            position="center"
          >
            <h2 className="modal-title">Confirmar eliminación</h2>
            <p>¿Estás seguro de que deseas eliminar este elemento?</p>
            <div className="modal-actions">
              <Button
                variant="filled"
                onClick={confirmDelete}
                endIcon={undefined}
                startIcon={undefined}
              >
                Confirmar
              </Button>
              <Button
                variant="outlined"
                onClick={() => setConfirmationModalOpen(false)}
                endIcon={undefined}
                startIcon={undefined}
              >
                Cancelar
              </Button>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default Table;
